import { createSelector } from 'reselect';
import { getValue } from '../constants/getFieldValues';
import { LIVING_TOGETHER, MARITAL_STATUS } from '../constants/applicant';
import {
  getPassportDueDate,
  getPassportStatus,
  isRussianPassport,
} from '../validators/common';
import { ApplicantFields } from '../models/applicant';
import { AddressFields } from '../../types/address';
import { LoadingState } from '../declarations/widget-ums';
import { getLoadingState } from './addressUnmodeled';
import { DocumentFields, DocumentTypeValue } from '../constants/document';
import { CHANGE_NAME } from '../constants/child';

export const getApplicant: (state: {
  applicant: ApplicantFields;
}) => ApplicantFields = (state) => state.applicant;

export const getApplicantSexValue: (state: {
  applicant: ApplicantFields;
}) => string = createSelector(getApplicant, ({ sex }) => getValue(sex));

export const getApplicantAddress: (state: {
  applicant: ApplicantFields;
}) => AddressFields = createSelector(getApplicant, ({ address }) => address);
export const getApplicantIsAddressCompleted: ((state: {
  applicant: ApplicantFields;
}) => boolean) & {
  resultFunc: (res: AddressFields) => boolean;
  recomputations: () => number;
  resetRecomputations: () => number;
} = createSelector(
  getApplicantAddress,
  ({ isAddressCompleted }) => isAddressCompleted
);

export const getApplicantMarried: (state: {
  applicant: ApplicantFields;
}) => MARITAL_STATUS | null = createSelector(getApplicant, ({ married }) =>
  getValue(married)
);

export const isApplicantMarried: (state: {
  applicant: ApplicantFields;
}) => boolean | null = createSelector(getApplicantMarried, (married) =>
  married ? married === MARITAL_STATUS.married : null
);

export const getApplicantLivingTogether: (state: {
  applicant: ApplicantFields;
}) => LIVING_TOGETHER | null = createSelector(
  getApplicant,
  ({ livingTogether }) => getValue(livingTogether)
);

export const getApplicantLivingTogetherToSubmit: (state: {
  applicant: ApplicantFields;
}) => boolean | null = createSelector(getApplicantLivingTogether, (value) =>
  value ? value === LIVING_TOGETHER.livingTogether : null
);

export const getApplicantLivesWithSecondParent: (state: {
  applicant: ApplicantFields;
}) => boolean = createSelector(
  getApplicantLivingTogether,
  (livingTogetherValue) =>
    livingTogetherValue === LIVING_TOGETHER.livingTogether
);

export const getApplicantMarriedOrLivingTogether: (state: {
  applicant: ApplicantFields;
}) => boolean = createSelector(
  isApplicantMarried,
  getApplicantLivesWithSecondParent,
  (isMarried, livingTogether) => isMarried || livingTogether
);
export const getApplicantMaritalStatus: (state: {
  applicant: ApplicantFields;
}) => boolean = createSelector(
  isApplicantMarried,
  getApplicantLivingTogether,
  (isMarried, livingTogether) => isMarried || !!livingTogether
);

export const getApplicantBirthDate: (state: {
  applicant: ApplicantFields;
}) => string = createSelector(getApplicant, ({ birthdate }) =>
  getValue(birthdate)
);

export const getApplicantChangeName: (state: {
  applicant: ApplicantFields;
}) => CHANGE_NAME | null = createSelector(getApplicant, ({ changeName }) =>
  getValue(changeName)
);

export const getApplicantDocument: (state: {
  applicant: ApplicantFields;
}) => DocumentFields = createSelector(
  getApplicant,
  ({ dul: document }: ApplicantFields) => {
    return Object.assign({}, document);
  }
);

export const getApplicantDocumentType: (state: {
  applicant: ApplicantFields;
}) => DocumentTypeValue = createSelector(getApplicantDocument, ({ type }) =>
  getValue<DocumentTypeValue>(type)
);

export const isApplicantDocumentIsRussianPassport: (state: {
  applicant: ApplicantFields;
}) => boolean = createSelector(getApplicantDocumentType, (type) =>
  isRussianPassport(type)
);

export const getApplicantDocumentNumber: (state: {
  applicant: ApplicantFields;
}) => string = createSelector(getApplicantDocument, ({ number }) =>
  getValue<string>(number)
);

export const getApplicantDocumentBirthPlace: (state: {
  applicant: ApplicantFields;
}) => string = createSelector(getApplicant, ({ birthPlace }) =>
  getValue(birthPlace)
);
export const getApplicantDocumentIssuer: (state: {
  applicant: ApplicantFields;
}) => string = createSelector(getApplicantDocument, ({ issuer }) =>
  getValue(issuer)
);

export const getApplicantDocumentIssueDate: (state: {
  applicant: ApplicantFields;
}) => string = createSelector(getApplicantDocument, ({ issueDate }) =>
  getValue(issueDate)
);

export const getApplicantDocumentDivisionCode: (state: {
  applicant: ApplicantFields;
}) => string = createSelector(getApplicantDocument, ({ divisionCode }) =>
  getValue(divisionCode)
);

export const getApplicantDateOfMarriage: (state: {
  applicant: ApplicantFields;
}) => string = createSelector(getApplicant, ({ dateOfMarriage }) =>
  getValue(dateOfMarriage)
);

export const getApplicantDateOfMarriageError: (state: {
  applicant: ApplicantFields;
}) => string | boolean | undefined = createSelector(
  getApplicant,
  ({ dateOfMarriage }) => dateOfMarriage.error
);

export const getApplicantMarriageRecordNumber: (state: {
  applicant: ApplicantFields;
}) => string = createSelector(getApplicant, ({ marriageRecordNumber }) =>
  getValue(marriageRecordNumber)
);

export const getApplicantMarriageRecordHolder: (state: {
  applicant: ApplicantFields;
}) => string = createSelector(getApplicant, ({ marriageRecordHolder }) =>
  getValue(marriageRecordHolder)
);

export const isAuthAndElkLoading: (state: {
  loading: LoadingState;
}) => boolean = createSelector(getLoadingState, ({ effects = {} }) =>
  [
    effects['auth/getUserAuth'],
    effects['elk/getBlocks'],
    effects['form/getDraft'],
  ].some(Boolean)
);

export const validateApplicantPassportStatus: (state: {
  applicant: ApplicantFields;
}) => string = createSelector(
  getApplicantBirthDate,
  getApplicantDocumentIssueDate,
  isApplicantDocumentIsRussianPassport,
  (birthDate, issueDate, isRussianPassport) =>
    getPassportStatus(birthDate, issueDate, isRussianPassport)
);

export const getApplicantPassportDueDate: (state: {
  applicant: ApplicantFields;
}) => string = createSelector(getApplicantBirthDate, (birthdate) =>
  getPassportDueDate(birthdate)
);

export const getApplicantPassportStatus: (state: {
  applicant: ApplicantFields;
}) => string = createSelector(
  getApplicant,
  ({ dul: { status } }: ApplicantFields) => {
    return status;
  }
);
